/**
 * ReasonHealthTab — Campus Profile > Reason Codes (PalCare only, read-only).
 *
 * Compares the community's reason codes in the two stores and against its events:
 *   - DynamoDB <prefix>CampusReasonCode: what the non-cloud mobile app offers caregivers;
 *   - MySQL core_reason_codes: what reports label events with.
 * Drift between them used to surface only as a customer complaint about mislabelled reports.
 *
 * Backed by mysql_fetch `reasoncodehealth`, the one-community version of
 * scripts/reason_code_audit.py. Finding codes and verdicts are the ones in
 * docs/REASON_CODE_FIX_PLAN.md. Nothing here changes data.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchReasonCodeHealth } from '../api/campusProfileApi.js';

/* Plain-language title per finding code; the detail line comes from the Lambda. */
const TITLES = {
    M1: 'Missing from reports',
    M2: 'Never migrated',
    M3: 'Deleted in reports only',
    M4: 'Deleted in reports, renamed in app',
    M5: 'Deleted in app only',
    M6: 'Spelling differs',
    M7: 'Name differs',
    M8: 'Category differs',
    M9: 'In reports only',
    M9b: 'App shows categories, reports show custom codes',
    M10: 'Duplicate rows (identical)',
    M11: 'Duplicate rows (different names)',
    M12: 'Number found in neither store',
    M13: 'Duplicate local id (cloud)',
    M14: 'Local id collides with a cloud id',
    M15: 'Cloud events with local ids',
    M16: 'Cloud events with no matching reason',
    M17: 'Leftover app rows on a cloud community',
    G16: 'Resident Trends naming risk',
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

/* Badge for the doc's verdicts. An M1 whose older-events check already passes is safe today. */
const verdictBadge = (f) => {
    if (f.verdict === 'auto' || (f.verdict === 'auto_after_fix' && f.auto_now)) {
        return { cls: 'dp-status dp-status--active', label: 'Safe to fix' };
    }
    if (f.verdict === 'info') return { cls: 'dp-status dp-status--inactive', label: 'Info' };
    return { cls: 'dp-status dp-status--warning', label: 'Needs review' };
};

const appCell = (f) => {
    if (!f.ddb) return <span className="dp-cell-text dp-cell-text--muted">{'—'}</span>;
    return (
        <span className="dp-cell-text">
            {f.ddb.description || '—'}
            <span className="dp-cell-text--muted"> · cat {f.ddb.category ?? '?'}{f.ddb.deleted ? ' · deleted' : ''}</span>
        </span>
    );
};

const reportsCell = (f) => {
    if (!f.mysql || f.mysql.length === 0) return <span className="dp-cell-text dp-cell-text--muted">{'—'}</span>;
    return (
        <span className="dp-cell-text">
            {f.mysql.map((r) => (
                <span key={r.cloud_reason_code_id} style={{ display: 'block' }}>
                    {r.reason_code_description || '—'}
                    <span className="dp-cell-text--muted">
                        {' '}· cat {r.pal_reason_code_id ?? '?'} · #{r.cloud_reason_code_id}
                        {r.deprecated_date ? ` · deleted ${String(r.deprecated_date).slice(0, 10)}` : ''}
                    </span>
                </span>
            ))}
        </span>
    );
};

const ReasonHealthTab = ({ campusId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        const res = await fetchReasonCodeHealth(campusId);
        if (res && Array.isArray(res.findings)) {
            setData(res);
        } else {
            setData(null);
            setError(true);
        }
        setLoading(false);
    }, [campusId]);

    useEffect(() => { load(); }, [load]);

    const findings = data?.findings || [];
    const summary = data?.summary || {};
    const isCloud = Boolean(data?.campus?.is_cloud);
    const needsReview = findings.filter((f) => verdictBadge(f).label === 'Needs review').length;
    const safe = findings.filter((f) => verdictBadge(f).label === 'Safe to fix').length;

    return (
        <section className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">
                    Reason Code Health
                    {data ? <span className="pp-section__count">({findings.length})</span> : null}
                </h3>
                <div className="pp-section__actions">
                    <button type="button" className="dp-btn dp-btn--secondary dp-btn--sm" onClick={load} disabled={loading}>
                        {loading ? 'Checking...' : 'Re-check'}
                    </button>
                </div>
            </div>
            <div className="pp-section__body" style={{ padding: '1rem 1.25rem' }}>
                <p className="dp-cell-text dp-cell-text--muted" style={{ margin: '0 0 0.75rem' }}>
                    {isCloud
                        ? 'Cloud community: reasons come only from the reports database (core_reason_codes). '
                        : 'Compares the reasons caregivers pick in the app (DynamoDB) with the reasons reports label events with (core_reason_codes). '}
                    Read-only. Nothing on this tab changes data.
                </p>

                {error ? (
                    <p className="dp-cell-text" style={{ color: 'var(--color-danger-text)' }}>
                        The reason code check could not be run. Try Re-check.
                    </p>
                ) : null}

                {data ? (
                    <>
                        {data.campus?.dynamo_error ? (
                            <p className="dp-cell-text" style={{ color: 'var(--color-warning-text-deep)' }}>
                                {data.campus.dynamo_error}
                            </p>
                        ) : null}
                        <p className="dp-cell-text" style={{ margin: '0 0 0.75rem' }}>
                            {!isCloud ? <>App (DynamoDB): {fmtNum(summary.ddb_live)} active, {fmtNum(summary.ddb_deleted)} deleted · </> : null}
                            Reports (MySQL): {fmtNum(summary.mysql_live)} active, {fmtNum(summary.mysql_deprecated)} deleted ·
                            Events with a reason: {fmtNum(summary.reason_events)}
                            {findings.length > 0 ? <> · <strong>{needsReview}</strong> need review, <strong>{safe}</strong> safe to fix</> : null}
                        </p>

                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead>
                                    <tr>
                                        <th className="dp-th">Issue</th>
                                        <th className="dp-th dp-th--num">Reason #</th>
                                        {!isCloud ? <th className="dp-th">In the app</th> : null}
                                        <th className="dp-th">In reports</th>
                                        <th className="dp-th dp-th--num">Events</th>
                                        <th className="dp-th dp-th--num">Last 90 days</th>
                                        <th className="dp-th">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {findings.length === 0 ? (
                                        <tr>
                                            <td className="dp-td" colSpan={isCloud ? 6 : 7}>
                                                <span className="dp-cell-text dp-cell-text--muted">
                                                    No reason code issues found.
                                                </span>
                                            </td>
                                        </tr>
                                    ) : findings.map((f, i) => {
                                        const badge = verdictBadge(f);
                                        return (
                                            <tr key={`${f.code}-${f.reason_id ?? 'c'}-${i}`}>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                                                        {TITLES[f.code] || f.code}
                                                        <span className="dp-cell-text--muted"> ({f.code})</span>
                                                    </span>
                                                    <span className="dp-cell-text dp-cell-text--muted" style={{ display: 'block' }}>
                                                        {f.detail}
                                                    </span>
                                                </td>
                                                <td className="dp-td dp-td--num">
                                                    <span className="dp-cell-text">{f.reason_id ?? '—'}</span>
                                                </td>
                                                {!isCloud ? <td className="dp-td">{appCell(f)}</td> : null}
                                                <td className="dp-td">{reportsCell(f)}</td>
                                                <td className="dp-td dp-td--num"><span className="dp-cell-text">{fmtNum(f.events)}</span></td>
                                                <td className="dp-td dp-td--num"><span className="dp-cell-text">{fmtNum(f.events_recent)}</span></td>
                                                <td className="dp-td"><span className={badge.cls}>{badge.label}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}
            </div>
        </section>
    );
};

export default ReasonHealthTab;
